import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LayoutGrid, Clock, Activity, LogOut, Plus, Globe, Check } from "lucide-react";
import { SUPPORTED_LANGUAGES } from "@/i18n";

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { t, i18n } = useTranslation();

  const NavLink = ({ to, icon: Icon, label, testId }) => {
    const active = loc.pathname === to;
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
          active ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        <Icon className="h-4 w-4" />
        {label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="nav-home">
              <Logo showSubtitle subtitle={t("auth.overline")} />
            </Link>
            <nav className="hidden items-center gap-1 md:flex">
              <NavLink to="/" icon={LayoutGrid} label={t("nav.workspace")} testId="nav-workspace" />
              <NavLink to="/timeline" icon={Clock} label={t("nav.timeline")} testId="nav-timeline" />
              {user?.role === "admin" && (
                <NavLink to="/admin" icon={Activity} label={t("nav.observability")} testId="nav-admin" />
              )}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              className="hidden bg-primary text-primary-foreground hover:opacity-90 sm:flex"
              onClick={() => nav("/")}
              data-testid="new-decision-nav-btn"
            >
              <Plus className="mr-1.5 h-4 w-4" /> {t("nav.newDecision")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-foreground text-sm font-semibold text-background"
                  data-testid="user-menu-trigger"
                >
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <Globe className="h-3.5 w-3.5" /> {t("nav.language")}
                </DropdownMenuLabel>
                {SUPPORTED_LANGUAGES.map((lng) => (
                  <DropdownMenuItem
                    key={lng.code}
                    onClick={() => i18n.changeLanguage(lng.code)}
                    data-testid={`lang-option-${lng.code}`}
                    className="cursor-pointer"
                  >
                    <span className="mr-2">{lng.flag}</span>
                    <span className="flex-1">{lng.label}</span>
                    {i18n.resolvedLanguage === lng.code && (
                      <Check className="h-4 w-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/timeline")}>
                  <Clock className="mr-2 h-4 w-4" /> {t("nav.timeline")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    nav("/login");
                  }}
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" /> {t("nav.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}
